import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Text } from '../../ui'
import { useAuthContext } from '../../../contexts/AuthContext'
import {
  SOCIAL_POST_IMAGE_MAX_BYTES,
  SOCIAL_POST_IMAGE_MIME_TYPES,
  SOCIAL_POST_MAX_IMAGES,
  SOCIAL_POST_MAX_LENGTH,
} from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import MentionTextarea from './MentionTextarea'
import { dashboardInsetPanelClass, feedCardClass } from './socialUtils'

export interface PostComposerProps {
  onSubmit: (content: string, files?: File[]) => void | Promise<void>
  placeholder?: string
  maxLength?: number
  embedded?: boolean
  /** Texto inicial (ex.: resultado do caça-palavras). Expande o compositor. */
  initialContent?: string
  /** Muda a cada compartilhamento para reaplicar o mesmo texto. */
  initialContentKey?: number
}

interface PreviewItem {
  id: string
  file: File
  url: string
}

const PostComposer: React.FC<PostComposerProps> = ({
  onSubmit,
  placeholder = 'Compartilhe boas notícias',
  maxLength = SOCIAL_POST_MAX_LENGTH,
  embedded = false,
  initialContent,
  initialContentKey,
}) => {
  const { profile } = useAuthContext()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const trimmedInitial = initialContent?.trim() ?? ''
  const [expanded, setExpanded] = useState(Boolean(trimmedInitial))
  const [content, setContent] = useState(trimmedInitial)
  const [previews, setPreviews] = useState<PreviewItem[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url))
    }
    // only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!initialContent?.trim()) return
    setContent(initialContent)
    setExpanded(true)
  }, [initialContent, initialContentKey])

  const clearPreviews = () => {
    setPreviews((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url))
      return []
    })
  }

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList?.length) return
    setError(null)

    const allowed = new Set<string>(SOCIAL_POST_IMAGE_MIME_TYPES)
    const incoming = Array.from(fileList)
    const next: PreviewItem[] = []
    let errMsg: string | null = null

    setPreviews((current) => {
      const remaining = SOCIAL_POST_MAX_IMAGES - current.length
      if (remaining <= 0) {
        errMsg = `Máximo de ${SOCIAL_POST_MAX_IMAGES} imagens por postagem.`
        return current
      }

      for (const file of incoming) {
        if (next.length >= remaining) {
          errMsg = `Máximo de ${SOCIAL_POST_MAX_IMAGES} imagens por postagem.`
          break
        }
        if (!allowed.has(file.type)) {
          errMsg = `Tipo não permitido: ${file.name}. Use JPEG, PNG, GIF ou WebP.`
          continue
        }
        if (file.size > SOCIAL_POST_IMAGE_MAX_BYTES) {
          const maxMb = SOCIAL_POST_IMAGE_MAX_BYTES / (1024 * 1024)
          errMsg = `A imagem "${file.name}" excede o limite de ${maxMb} MB.`
          continue
        }
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          url: URL.createObjectURL(file),
        })
      }

      return next.length ? [...current, ...next] : current
    })

    if (errMsg) setError(errMsg)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removePreview = (id: string) => {
    setPreviews((prev) => {
      const target = prev.find((p) => p.id === id)
      if (target) URL.revokeObjectURL(target.url)
      return prev.filter((p) => p.id !== id)
    })
    if (error) setError(null)
  }

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed && previews.length === 0) {
      setError('Escreva algo ou anexe ao menos uma imagem.')
      return
    }
    if (trimmed.length > maxLength) {
      setError(`Máximo de ${maxLength} caracteres.`)
      return
    }

    try {
      setSubmitting(true)
      setError(null)
      const files = previews.map((p) => p.file)
      await onSubmit(trimmed, files.length ? files : undefined)
      setContent('')
      clearPreviews()
      setExpanded(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao publicar')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    setContent('')
    clearPreviews()
    setExpanded(false)
    setError(null)
  }

  const canPublish = Boolean(content.trim() || previews.length > 0)

  return (
    <div className={embedded ? dashboardInsetPanelClass : feedCardClass}>
      <div className="p-4">
        <div className="flex gap-3">
          <AuthorAvatar
            name={profile?.name}
            avatarPath={profile?.avatar_url}
          />
          <div className="flex-1 min-w-0">
            {!expanded ? (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className={clsx(
                  'w-full text-left rounded-full px-4 py-2.5 text-sm',
                  'bg-gray-100 dark:bg-gray-700/80 text-gray-500 dark:text-gray-400',
                  'hover:bg-gray-200/80 dark:hover:bg-gray-700 transition-colors'
                )}
              >
                {placeholder}
              </button>
            ) : (
              <div className="space-y-3">
                <MentionTextarea
                  value={content}
                  onChange={(v) => {
                    setContent(v)
                    if (error) setError(null)
                  }}
                  maxLength={maxLength}
                  placeholder={`${placeholder} Use @ para marcar alguém.`}
                  rows={3}
                  disabled={submitting}
                  autoFocus
                />

                {previews.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {previews.map((p) => (
                      <div
                        key={p.id}
                        className="relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700"
                      >
                        <img
                          src={p.url}
                          alt={p.file.name}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePreview(p.id)}
                          disabled={submitting}
                          className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80 disabled:opacity-50"
                          aria-label={`Remover ${p.file.name}`}
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={SOCIAL_POST_IMAGE_MIME_TYPES.join(',')}
                      multiple
                      className="hidden"
                      disabled={submitting || previews.length >= SOCIAL_POST_MAX_IMAGES}
                      onChange={(e) => handleFilesSelected(e.target.files)}
                    />
                    <button
                      type="button"
                      disabled={submitting || previews.length >= SOCIAL_POST_MAX_IMAGES}
                      onClick={() => fileInputRef.current?.click()}
                      className={clsx(
                        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors',
                        'text-primary-600 dark:text-primary-400',
                        'hover:bg-primary-50 dark:hover:bg-primary-900/20',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                      title="Adicionar imagens"
                    >
                      <PhotoIcon className="h-5 w-5" />
                      <span className="hidden sm:inline">Imagem</span>
                    </button>
                    <Text size="xs" color="muted" className="tabular-nums">
                      {content.length}/{maxLength}
                    </Text>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCancel} disabled={submitting}>
                      Cancelar
                    </Button>
                    <Button
                      variant="solid"
                      disabled={submitting || !canPublish}
                      onClick={handleSubmit}
                    >
                      {submitting ? 'Publicando...' : 'Publicar'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {error && (
          <Text size="xs" className="mt-2 ml-[3.25rem] text-red-600 dark:text-red-400">
            {error}
          </Text>
        )}
      </div>
    </div>
  )
}

export default PostComposer
